"use client";

import { ImageIcon, X } from "lucide-react";
import { useState } from "react";
import { BlobImage } from "@/components/BlobImage";
import { primaryButtonClass } from "@/components/form";
import { ProgressBar } from "@/components/ProgressBar";
import { Segmented } from "@/components/Segmented";
import { moveItem, SortableList } from "@/components/SortableList";
import { ToolShell, type ToolResult } from "@/components/ToolShell";
import { useImageSizes } from "@/hooks/useImageSizes";
import { useToolFiles } from "@/hooks/useToolFiles";
import { formatBytes, outputName } from "@/lib/files";
import {
  pageFor,
  placeImage,
  type ImagesToPdfOptions,
  type Margin,
  type Orientation,
  type PageSize,
} from "@/lib/pdf/layout";
import { getTool } from "@/lib/tools";
import { imagesToPdfFile } from "@/workers/pdf-client";

const tool = getTool("jpg-to-pdf")!;

const fileKey = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;

/** FR-16: reorder images; A4/Letter/fit; portrait/landscape; margins. */
export function ImagesToPdfTool() {
  const { files, setFiles, notices } = useToolFiles(tool);
  const sizes = useImageSizes(files);
  const [options, setOptions] = useState<ImagesToPdfOptions>({
    pageSize: "a4",
    orientation: "portrait",
    margin: "small",
  });
  const [results, setResults] = useState<ToolResult[] | null>(null);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [error, setError] = useState("");
  const set = (patch: Partial<ImagesToPdfOptions>) => {
    setOptions((o) => ({ ...o, ...patch }));
    setResults(null);
  };

  function change(next: File[]) {
    setFiles(next);
    setResults(null);
    setError("");
  }

  async function create() {
    setError("");
    setProgress({ done: 0, total: files.length });
    try {
      const blob = await imagesToPdfFile(files, options, (done) =>
        setProgress({ done, total: files.length }),
      );
      setResults([
        {
          blob,
          name: outputName(
            files[0].name,
            files.length > 1 ? "combined" : "converted",
            "pdf",
          ),
          detail: `${files.length} ${files.length === 1 ? "page" : "pages"}`,
        },
      ]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setProgress(null);
    }
  }

  const first = files[0] ? sizes.get(files[0]) : undefined;

  return (
    <ToolShell
      tool={tool}
      files={files}
      onFilesChange={change}
      notices={notices}
      results={results}
      onStartOver={() => change([])}
      fileList={
        <SortableList
          label="Images, in page order"
          items={files}
          itemKey={fileKey}
          itemLabel={(f) => f.name}
          onReorder={(from, to) => change(moveItem(files, from, to))}
          renderItem={(file, i, { handle, moveButtons }) => {
            const size = sizes.get(file);
            return (
              <div className="flex items-center gap-2 rounded-lg border bg-card p-1.5 sm:gap-3">
                {handle}
                <span className="w-6 shrink-0 text-center font-heading text-lg text-muted-foreground">
                  {i + 1}
                </span>
                <Thumb file={file} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-semibold" title={file.name}>
                    {file.name}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {size ? `${size.width} × ${size.height} px · ` : ""}
                    {formatBytes(file.size)}
                  </span>
                </span>
                <span className="hidden sm:flex">{moveButtons}</span>
                <button
                  type="button"
                  onClick={() => change(files.filter((_, j) => j !== i))}
                  aria-label={`Remove ${file.name}`}
                  className="flex size-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-sand hover:text-ink"
                >
                  <X className="size-5" aria-hidden="true" />
                </button>
              </div>
            );
          }}
        />
      }
    >
      <section
        aria-label="Settings"
        className="grid gap-5 rounded-[16px] border bg-card p-4 sm:p-5 md:grid-cols-[minmax(0,1fr)_200px]"
      >
        <div className="flex min-w-0 flex-col gap-5">
          <Choice label="Page size">
            <Segmented
              label="Page size"
              stretch
              value={options.pageSize}
              onChange={(pageSize: PageSize) => set({ pageSize })}
              options={[
                { value: "a4", label: "A4" },
                { value: "letter", label: "Letter" },
                { value: "fit", label: "Fit image" },
              ]}
            />
          </Choice>
          {options.pageSize !== "fit" && (
            <Choice label="Orientation">
              <Segmented
                label="Orientation"
                stretch
                value={options.orientation}
                onChange={(orientation: Orientation) => set({ orientation })}
                options={[
                  { value: "portrait", label: "Portrait" },
                  { value: "landscape", label: "Landscape" },
                ]}
              />
            </Choice>
          )}
          <Choice label="Margins">
            <Segmented
              label="Margins"
              stretch
              value={options.margin}
              onChange={(margin: Margin) => set({ margin })}
              options={[
                { value: "none", label: "None" },
                { value: "small", label: "Small" },
                { value: "large", label: "Large" },
              ]}
            />
          </Choice>
        </div>
        {first && files[0] && (
          <PagePreview
            key={fileKey(files[0])}
            file={files[0]}
            image={first}
            options={options}
          />
        )}

        <div className="flex flex-col gap-2 md:col-span-2">
          {error && (
            <p role="alert" className="font-medium text-destructive">
              {error}
            </p>
          )}
          {progress ? (
            <ProgressBar
              label={`Preparing image ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…`}
              value={progress.done}
              max={progress.total}
            />
          ) : (
            <button
              type="button"
              onClick={create}
              disabled={files.length === 0}
              className={primaryButtonClass}
            >
              Create PDF ({files.length} {files.length === 1 ? "page" : "pages"}
              )
            </button>
          )}
          <p className="text-center text-[13px] text-muted-foreground">
            Nothing is uploaded
          </p>
        </div>
      </section>
    </ToolShell>
  );
}

function Choice({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-semibold">{label}</span>
      {children}
    </div>
  );
}

/** A small drawing of page 1 with the image where it will go. */
function PagePreview({
  file,
  image,
  options,
}: {
  file: File;
  image: { width: number; height: number };
  options: ImagesToPdfOptions;
}) {
  const [broken, setBroken] = useState(false);
  const page = pageFor(image, options);
  const box = placeImage(page, image, options.margin);
  const pct = (v: number, of: number) => `${(v / of) * 100}%`;
  return (
    <figure className="flex flex-col items-center gap-2" aria-hidden="true">
      <div
        className="relative w-full max-w-[180px] bg-white shadow ring-1 ring-line"
        style={{ aspectRatio: `${page.width} / ${page.height}` }}
      >
        <div
          className="absolute overflow-hidden bg-image-tint"
          style={{
            left: pct(box.x, page.width),
            // PDF y counts from the bottom.
            bottom: pct(box.y, page.height),
            width: pct(box.width, page.width),
            height: pct(box.height, page.height),
          }}
        >
          {!broken && (
            <BlobImage
              blob={file}
              alt=""
              onError={() => setBroken(true)}
              className="size-full"
            />
          )}
        </div>
      </div>
      <figcaption className="text-[13px] text-muted-foreground">
        Page 1
      </figcaption>
    </figure>
  );
}

function Thumb({ file }: { file: File }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded border bg-sand">
      {broken ? (
        <ImageIcon className="size-5 text-image" aria-hidden="true" />
      ) : (
        <BlobImage
          blob={file}
          alt=""
          onError={() => setBroken(true)}
          className="size-full object-cover"
        />
      )}
    </span>
  );
}
